import { Children, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Markdown renderer for assistant output in the playground.
 *
 * Renders GFM (headings, tables, lists, bold/italic, code, hr) styled to
 * the playground design tokens — so model output reads cleanly instead of
 * raw ``#`` / ``|`` / ``**`` source. Governance references
 * (``SOP-…`` / ``MGO-…`` / ``POJK n/n``) are still surfaced as blue
 * pills inside any text run, preserving the prior FormattedContent rule.
 */

const PILL_PATTERN = /(?:SOP|MGO)-[A-Z0-9][A-Z0-9-]*|POJK\s+\d+\/\d+/g;

function splitPills(text: string): ReactNode {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  PILL_PATTERN.lastIndex = 0;
  while ((match = PILL_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) out.push(text.slice(lastIndex, match.index));
    out.push(
      <span key={`pill-${key++}`} className="pill pill--blue mx-[2px]">
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out.length === 1 ? out[0] : out;
}

/** Apply the governance-pill transform to any plain-string children,
 *  passing already-rendered element children through untouched. */
function pillify(children: ReactNode): ReactNode {
  return Children.map(children, (child) =>
    typeof child === "string" ? splitPills(child) : child
  );
}

const components: Components = {
  h1: ({ children }) => (
    <h1 className="mb-2 mt-4 text-base font-semibold first:mt-0">
      {pillify(children)}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 text-[14.5px] font-semibold first:mt-0">
      {pillify(children)}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1.5 mt-3 text-[13.5px] font-semibold first:mt-0">
      {pillify(children)}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-1.5 mt-3 text-[13px] font-semibold first:mt-0">
      {pillify(children)}
    </h4>
  ),
  p: ({ children }) => (
    <p className="my-2 leading-relaxed first:mt-0 last:mb-0">
      {pillify(children)}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="my-2 list-disc space-y-1 pl-5 first:mt-0 last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5 first:mt-0 last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{pillify(children)}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{pillify(children)}</strong>
  ),
  em: ({ children }) => <em className="italic">{pillify(children)}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
      style={{ color: "var(--pg-blue-dk)" }}
    >
      {children}
    </a>
  ),
  hr: () => (
    <hr className="my-3 border-0 border-t" style={{ borderColor: "var(--pg-border)" }} />
  ),
  blockquote: ({ children }) => (
    <blockquote
      className="my-2 border-l-2 pl-3 italic"
      style={{ borderColor: "var(--pg-border)", color: "var(--pg-text-2)" }}
    >
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const text = String(children ?? "");
    const isBlock = /language-/.test(className ?? "") || text.includes("\n");
    if (isBlock) {
      return <code className="mono block text-xs leading-relaxed">{children}</code>;
    }
    return (
      <code
        className="mono rounded px-1 py-[1px] text-[0.85em]"
        style={{ background: "var(--pg-bg)", border: "1px solid var(--pg-border)" }}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre
      className="my-2 overflow-x-auto rounded-md p-3 first:mt-0 last:mb-0"
      style={{ background: "var(--pg-bg)", border: "1px solid var(--pg-border)" }}
    >
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto first:mt-0 last:mb-0">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ background: "var(--pg-bg)" }}>{children}</thead>
  ),
  th: ({ children }) => (
    <th
      className="px-2.5 py-1.5 text-left font-semibold"
      style={{ border: "1px solid var(--pg-border)" }}
    >
      {pillify(children)}
    </th>
  ),
  td: ({ children }) => (
    <td
      className="px-2.5 py-1.5 align-top"
      style={{ border: "1px solid var(--pg-border)" }}
    >
      {pillify(children)}
    </td>
  ),
};

export function Markdown({ text }: { text: string }) {
  return (
    // Reset the bubble's ``white-space: pre-wrap`` for the rendered tree
    // so block elements don't inherit source-newline gaps.
    <div className="whitespace-normal text-[13.5px]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
